import { type CandidateStatEntry, useBubbleGetCandidateStats, useBubbleGetSiteId } from "@crowdcoursing/api/openapi";
import { keepPreviousData } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { type DateRangeValue } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { StudentCandidacyDashboard } from "~ui/templates/AdminTemplate/DashboardStats/StudentCandidacyDashboard";

/**
 * Aggregates candidate stats by date, summing metrics for entries with the same date.
 * This handles the case where multiple bubbles may have stats for the same date.
 */
const aggregateStatsByDate = (stats: CandidateStatEntry[] | null | undefined): CandidateStatEntry[] => {
  if (!stats || stats.length === 0) {
    return [];
  }

  // Group by date (YYYY-MM-DD format)
  const statsByDate = new Map<string, CandidateStatEntry>();

  stats.forEach((stat) => {
    if (!stat.timestamp) {
      return;
    }

    const date = DateTime.fromISO(stat.timestamp).toFormat("yyyy-MM-dd");
    const existing = statsByDate.get(date);

    if (existing && existing.metrics && stat.metrics) {
      // Aggregate metrics by summing values
      existing.metrics = {
        studentsPlaced: (existing.metrics.studentsPlaced || 0) + (stat.metrics.studentsPlaced || 0),
        studentsWithInterest: (existing.metrics.studentsWithInterest || 0) + (stat.metrics.studentsWithInterest || 0),
        studentsWithInterviewRequests:
          (existing.metrics.studentsWithInterviewRequests || 0) + (stat.metrics.studentsWithInterviewRequests || 0),
        studentsWithInterviewsAttended:
          (existing.metrics.studentsWithInterviewsAttended || 0) + (stat.metrics.studentsWithInterviewsAttended || 0),
        studentsWithResume: (existing.metrics.studentsWithResume || 0) + (stat.metrics.studentsWithResume || 0),
        totalStudents: (existing.metrics.totalStudents || 0) + (stat.metrics.totalStudents || 0),
      };
    } else {
      // First entry for this date
      statsByDate.set(date, {
        metrics: { ...stat.metrics },
        timestamp: stat.timestamp,
      });
    }
  });

  // Convert back to array and sort by date
  return Array.from(statsByDate.values()).sort((a, b) => (a.timestamp ?? "").localeCompare(b.timestamp ?? ""));
};

const START_DATE_STORAGE_KEY = "studentCandidacyTab.startDate";

const getInitialTimeRange = (): DateRangeValue => {
  try {
    const stored = localStorage.getItem(START_DATE_STORAGE_KEY);
    if (stored) {
      const startDate = DateTime.fromISO(stored);
      if (startDate?.isValid) {
        return { endDate: DateTime.now(), startDate };
      }
    }
  } catch {
    // Fall through to default
  }
  return {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ months: 1 }),
  };
};

const useStudentCandidacyDashboardProps = () => {
  const { data: bubbleId } = useBubbleGetSiteId({});
  const [timeRange, setTimeRange] = useState<DateRangeValue>(getInitialTimeRange);

  const handleTimeRangeChange = (newTimeRange: DateRangeValue) => {
    setTimeRange(newTimeRange);
    if (newTimeRange.startDate) {
      localStorage.setItem(START_DATE_STORAGE_KEY, newTimeRange.startDate.toISO() ?? "");
    }
  };

  const dateParams = useMemo(
    () => ({
      endDate: timeRange.endDate?.toISO() ?? DateTime.now().toISO(),
      isAggregate: true, // Get aggregate stats from backend, then combine by date on frontend
      startDate: timeRange.startDate?.toISO() ?? DateTime.now().toISO(),
    }),
    [timeRange],
  );

  const { data, status } = useBubbleGetCandidateStats(
    {
      pathParams: { bubbleId: bubbleId ?? "" },
      queryParams: dateParams,
    },
    { enabled: !!bubbleId, placeholderData: keepPreviousData },
  );

  // Aggregate stats by date on the frontend
  const aggregatedMetrics = useMemo(() => aggregateStatsByDate(data?.stats), [data?.stats]);

  return {
    bubbleId: bubbleId ?? "",
    metrics: aggregatedMetrics,
    onTimeRangeChange: handleTimeRangeChange,
    status,
  };
};

export const StudentCandidacyTab = () => {
  const props = useStudentCandidacyDashboardProps();
  return <StudentCandidacyDashboard {...props} />;
};
